import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Blocks, Boxes, BriefcaseBusiness, Check, ChevronRight, Compass, Download, GraduationCap, LayoutGrid, LogOut, Moon, Plus, Search, Settings, Share2, ShoppingBag, Smartphone, Sparkles, Sun, UserRound, UsersRound, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { supabase } from '../../lib/supabase';
import { SearchDropdown } from '../SearchDropdown';
import { StoreOnboarding } from './StoreOnboarding';
import './storeExperience.css';

const categories = [
  { label: 'Human Made', href: '/human-made', icon: UserRound, detail: 'Handcrafted by independent creators' },
  { label: 'Digital products', href: '/digital-products', icon: Boxes, detail: 'Tools, templates & downloads' },
  { label: 'Services', href: '/services', icon: BriefcaseBusiness, detail: 'Find your next expert' },
  { label: 'Vacatures & Jobs', href: '/jobs', icon: BriefcaseBusiness, detail: 'Vind je rol of neem talent aan' },
  { label: 'Courses', href: '/courses', icon: GraduationCap, detail: 'A little more know-how' },
  { label: 'Communities', href: '/communities', icon: UsersRound, detail: 'Find your people' },
  { label: '3D assets', href: '/search?q=3D', icon: Blocks, detail: 'Build something new' },
  { label: 'Apps & software', href: '/search?q=software', icon: Sparkles, detail: 'Make work feel easier' },
];

export function StoreNavigation() {
  const { user, signOut } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [profileOpen, setProfileOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [profile, setProfile] = useState<{ avatar_url?: string; full_name?: string; username?: string } | null>(null);
  const [androidRelease, setAndroidRelease] = useState<{
    version: string;
    href: string;
    filename: string;
    size: string;
  }>({
    version: '0.7.8',
    href: '/downloads/Wersee-Android-0.7.6-preview.apk',
    filename: 'Wersee-Android-0.7.6-preview.apk',
    size: '246.7 MB',
  });
  const categoryButton = useRef<HTMLButtonElement>(null);
  const categoryPanel = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLDivElement>(null);
  const searchButton = useRef<HTMLButtonElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const profileMenu = useRef<HTMLDivElement>(null);
  const profileButton = useRef<HTMLButtonElement>(null);
  const downloadButton = useRef<HTMLButtonElement>(null);
  const downloadMenu = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let active = true;
    setProfile(null);
    if (user) void supabase.from('profiles').select('avatar_url, full_name, username').eq('id', user.id).maybeSingle()
      .then(({ data }) => { if (active) setProfile(data); });
    return () => { active = false; };
  }, [user?.id]);

  useEffect(() => {
    let active = true;
    void supabase
      .from('android_releases')
      .select('version_name, public_download_url, strato_public_url, filename, file_size')
      .eq('is_latest', true)
      .maybeSingle()
      .then(({ data }) => {
        if (!active || !data) return;
        setAndroidRelease({
          version: data.version_name || '0.7.8',
          href: data.public_download_url || data.strato_public_url || '/downloads/Wersee-Android-0.7.6-preview.apk',
          filename: data.filename || 'Wersee-Android.apk',
          size: data.file_size ? `${(data.file_size / (1024 * 1024)).toFixed(1)} MB` : '246.7 MB',
        });
      }, () => {});
    return () => { active = false; };
  }, []);

  useEffect(() => { setCategoriesOpen(false); setSearchOpen(false); setProfileOpen(false); setDownloadOpen(false); }, [location.pathname, location.search]);
  useEffect(() => { if (!location.state?.storeCheckoutBackground) window.scrollTo({ top: 0, left: 0, behavior: 'instant' }); }, [location.pathname]);
  useEffect(() => {
    if (searchOpen) searchInput.current?.focus();
  }, [searchOpen]);
  useEffect(() => {
    if (categoriesOpen) categoryPanel.current?.querySelector<HTMLAnchorElement>('a')?.focus();
  }, [categoriesOpen]);
  useEffect(() => {
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!categoryPanel.current?.contains(target) && !categoryButton.current?.contains(target)) setCategoriesOpen(false);
      if (!search.current?.contains(target)) setSearchOpen(false);
      if (!profileMenu.current?.contains(target) && !profileButton.current?.contains(target)) setProfileOpen(false);
      if (!downloadMenu.current?.contains(target) && !downloadButton.current?.contains(target)) setDownloadOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (categoriesOpen) { setCategoriesOpen(false); categoryButton.current?.focus(); }
      if (searchOpen) { setSearchOpen(false); searchButton.current?.focus(); }
      if (profileOpen) { setProfileOpen(false); profileButton.current?.focus(); }
      if (downloadOpen) { setDownloadOpen(false); downloadButton.current?.focus(); }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
  }, [categoriesOpen, searchOpen, profileOpen, downloadOpen]);

  useEffect(() => { if (profileOpen) profileMenu.current?.querySelector<HTMLAnchorElement>('a')?.focus(); }, [profileOpen]);
  useEffect(() => { if (downloadOpen) downloadMenu.current?.querySelector<HTMLElement>('a, button')?.focus(); }, [downloadOpen]);

  const avatar = profile?.avatar_url || user?.user_metadata?.avatar_url;
  const name = profile?.full_name || profile?.username || user?.user_metadata?.full_name || 'Your profile';
  const profilePath = profile?.username ? `/@${encodeURIComponent(profile.username)}` : '/profile';
  const shareProfile = async () => {
    const url = new URL(profilePath, window.location.origin).href;
    try {
      if (navigator.share) await navigator.share({ title: `${name} on Wersee`, url });
      else { await navigator.clipboard.writeText(url); toast.success('Profile link copied'); }
      setProfileOpen(false);
    } catch (error) { if (!(error instanceof DOMException && error.name === 'AbortError')) toast.error('Could not share your profile. Please try again.'); }
  };
  return <>
    <aside className="ws-store-sidebar" aria-label="Wersee navigation">
      <Link to="/" className="ws-store-brand" aria-label="Wersee home"><img src="/brand/wersee-icon-192.png" alt="" /></Link>
      <nav className="ws-store-nav" aria-label="Discover">
        <Link to="/" className={`ws-store-nav-item ${location.pathname === '/' ? 'is-active' : ''}`} aria-current={location.pathname === '/' ? 'page' : undefined}><Compass /><span>Discover</span></Link>
        <button ref={categoryButton} type="button" className={`ws-store-nav-item ${categoriesOpen ? 'is-active' : ''}`} aria-expanded={categoriesOpen} aria-controls="store-categories" onClick={() => { setCategoriesOpen(!categoriesOpen); setSearchOpen(false); setDownloadOpen(false); }}><LayoutGrid /><span>Categories</span></button>
      </nav>
      <div className="ws-store-nav-bottom">
        <button
          ref={downloadButton}
          type="button"
          className={`ws-store-download-trigger ${downloadOpen ? 'is-active' : ''}`}
          aria-label="Download Wersee voor Android"
          title="Download Wersee voor Android"
          aria-expanded={downloadOpen}
          aria-controls="store-download-menu"
          onClick={() => {
            setDownloadOpen(!downloadOpen);
            setCategoriesOpen(false);
            setSearchOpen(false);
            setProfileOpen(false);
          }}
        >
          <Smartphone />
        </button>
        <Link to="/workspace/overview" className="ws-store-nav-item" title="Your workspace"><BriefcaseBusiness /><span>Workspace</span></Link>
        <button ref={profileButton} type="button" className="ws-store-profile" aria-label={`${name} account menu`} aria-expanded={profileOpen} aria-controls="store-profile-menu" onClick={() => { setProfileOpen(!profileOpen); setCategoriesOpen(false); setSearchOpen(false); setDownloadOpen(false); }}>{avatar ? <img src={avatar} alt="" referrerPolicy="no-referrer" /> : <span>{String(name).slice(0, 1).toUpperCase()}</span>}<span className="ws-store-profile-label">Profile</span></button>
      </div>
    </aside>
    <Link to="/create" className={`ws-store-create-pill ${location.pathname.startsWith('/create') ? 'is-active' : ''}`}><Plus /><span>Create</span></Link>
    <AnimatePresence>{profileOpen && <motion.div ref={profileMenu} id="store-profile-menu" className="ws-store-account-menu" initial={{ opacity: 0, x: reduceMotion ? 0 : -8, scale: .98 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, scale: .98 }} transition={{ duration: .18 }}><p>{name}</p><Link to={profilePath}><UserRound />My profile</Link><button type="button" onClick={() => void shareProfile()}><Share2 />Share profile</button><Link to="/workspace/account-settings"><Settings />Settings</Link><button type="button" onClick={toggleTheme}>{isDark ? <Sun /> : <Moon />}{isDark ? 'Light appearance' : 'Dark appearance'}</button><button type="button" disabled={signingOut} onClick={async () => { setSigningOut(true); try { await signOut(); navigate('/'); } catch { toast.error('Could not log out. Please try again.'); } finally { setSigningOut(false); } }}><LogOut />{signingOut ? 'Logging out…' : 'Log out'}</button></motion.div>}</AnimatePresence>

    <AnimatePresence>
      {downloadOpen && (
        <motion.div
          ref={downloadMenu}
          id="store-download-menu"
          className="ws-store-download-menu"
          role="dialog"
          aria-label="Download Wersee voor Android"
          initial={{ opacity: 0, x: reduceMotion ? 0 : -8, scale: .98 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, scale: .98 }}
          transition={{ duration: .18 }}
        >
          <div className="ws-download-menu-header">
            <div className="ws-download-menu-badge">
              <Smartphone className="w-5 h-5" />
            </div>
            <div className="ws-download-menu-titles">
              <div className="flex items-center gap-1.5">
                <strong>Wersee Android</strong>
                <span className="ws-download-version-tag">v{androidRelease.version}</span>
              </div>
              <small>Officiële app · Direct APK</small>
            </div>
            <button
              type="button"
              className="ws-download-menu-close"
              aria-label="Sluit download menu"
              onClick={() => { setDownloadOpen(false); downloadButton.current?.focus(); }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <p className="ws-download-menu-desc">
            Neem je Wersee workspace, AI companion, cursussen en chats mee op je Android-toestel. Direct te downloaden en installeren.
          </p>

          <a
            href={androidRelease.href}
            download={androidRelease.filename}
            className="ws-download-action-btn"
            onClick={() => {
              toast.success('Download gestart! Open het APK-bestand in je downloads om te installeren.');
              setDownloadOpen(false);
            }}
          >
            <Download className="w-5 h-5 shrink-0" />
            <div className="flex flex-col text-left">
              <span className="font-bold text-sm leading-tight text-white">Download APK</span>
              <span className="text-[11px] opacity-85 text-white/90">{androidRelease.size} · Android 8.0+</span>
            </div>
          </a>

          <div className="ws-download-features">
            <div className="ws-download-feature-item">
              <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span>Directe APK installatie zonder store</span>
            </div>
            <div className="ws-download-feature-item">
              <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span>Offline cursussen en bestanden</span>
            </div>
            <div className="ws-download-feature-item">
              <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <span>Veilige, geverifieerde versie</span>
            </div>
          </div>

          <div className="ws-download-menu-footer">
            <Link
              to="/download"
              onClick={() => setDownloadOpen(false)}
              className="ws-download-guide-link"
            >
              <span>Installatie-instructies & details</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>

    <AnimatePresence>
      {categoriesOpen && <motion.div ref={categoryPanel} id="store-categories" className="ws-store-category-panel" initial={{ opacity: 0, x: reduceMotion ? 0 : -12, scale: reduceMotion ? 1 : .97 }} animate={{ opacity: 1, x: 0, scale: 1 }} exit={{ opacity: 0, x: reduceMotion ? 0 : -8 }} transition={{ duration: .2 }}>
        <div className="ws-store-popover-heading"><span>A world of possibilities.</span><button type="button" aria-label="Close categories" onClick={() => { setCategoriesOpen(false); categoryButton.current?.focus(); }}><X /></button></div>
        <nav aria-label="Product categories">{categories.map(({ label, href, icon: Icon, detail }) => <Link key={label} to={href}><span className="ws-store-category-icon"><Icon /></span><span><strong>{label}</strong><small>{detail}</small></span><ChevronRight /></Link>)}</nav>
        <Link className="ws-store-browse-all" to="/search"><ShoppingBag /> Explore everything <ChevronRight /></Link>
      </motion.div>}
    </AnimatePresence>

    <div ref={search} className={`ws-store-floating-search ${searchOpen ? 'is-open' : ''}`}>
      <motion.form role="search" className="ws-store-search-pill" animate={{ width: searchOpen ? '100%' : 48 }} transition={reduceMotion ? { duration: 0 } : { type: 'spring', stiffness: 340, damping: 32 }} onSubmit={(event) => { event.preventDefault(); navigate(query.trim() ? `/search?q=${encodeURIComponent(query.trim())}` : '/search'); setSearchOpen(false); }}>
        <button ref={searchButton} type={searchOpen ? 'submit' : 'button'} aria-label={searchOpen ? 'Search marketplace' : 'Open search'} aria-expanded={searchOpen} onClick={(event) => { if (!searchOpen) { event.preventDefault(); setSearchOpen(true); setCategoriesOpen(false); } }}><Search /></button>
        {searchOpen && <><label className="sr-only" htmlFor="store-global-search">Search Wersee</label><input ref={searchInput} id="store-global-search" type="search" autoComplete="off" placeholder="Search Wersee…" value={query} onChange={(event) => setQuery(event.target.value)} /><button type="button" aria-label="Close search" onClick={() => { setSearchOpen(false); searchButton.current?.focus(); }}><X /></button></>}
      </motion.form>
      <SearchDropdown isOpen={searchOpen} query={query} onClose={() => setSearchOpen(false)} />
    </div>
    {user && <StoreOnboarding userId={user.id} />}
  </>;
}
